import React, { ComponentProps, useCallback, useMemo } from 'react'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import TextButton from 'LuxKit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { pluralizeToString } from 'lib/string/pluralize'
import { useMultiRoomBookingGuard } from 'businessTraveller/hooks/useMultiRoomBookingGuard'

interface Props extends Omit<ComponentProps<typeof TextButton>, 'kind' | 'children'> {
  rooms: Array<App.RoomOccupants>;
  checkIn?: string;
  roomType?: App.PackageRoomType;
  packageOptions: Array<OfferPackageOptionView>;
  isFlightSelected?: boolean;
  activeFilter?: OfferPageFilterType;
  onSubmit?: () => void;
  durationSelected?: boolean;
}

function FlashHotelAvailabilityButton(props: Props) {
  const {
    rooms,
    checkIn,
    roomType,
    packageOptions,
    isFlightSelected,
    activeFilter,
    onSubmit,
    onClick,
    durationSelected,
    ...rest
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const guardBookingFlow = useMultiRoomBookingGuard()

  const invalidRoomSelection = !rooms || isRoomsInvalid(rooms)
  const canViewOptions = !!checkIn && !invalidRoomSelection
  const ctaLabel = useMemo(() => {
    if (canViewOptions) {
      if (rooms.length > 1) {
        return `View ${pluralizeToString('room', rooms.length)}`
      } else {
        return 'View rooms'
      }
    }
    return 'Check availability'
  }, [canViewOptions, rooms])

  const onClickAvailability = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (guardBookingFlow()) return
    onClick?.(e)
    if (invalidRoomSelection || !durationSelected) {
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'capacity',
        flights: !!isFlightSelected,
      })
      return
    }

    if (!checkIn) {
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'checkin',
        flights: !!isFlightSelected,
      })

      return
    }

    // Reset previous room selected
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: undefined })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'roomSelection' })
  }, [guardBookingFlow, checkIn, durationSelected, invalidRoomSelection, isFlightSelected, offerPageDispatch, onClick])

  return (
    <TextButton {...rest} kind="primary" onClick={onClickAvailability}>
      {ctaLabel}
    </TextButton>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  packageOptions: getDurationPackageOptions(state, appState),
  isFlightSelected: state.flights,
  activeFilter: state.activeFilter,
  durationSelected: state.durationSelected,
}), FlashHotelAvailabilityButton)
